import './RigCalibration.css';
import { InferenceResult } from '@/models/models';
import { fetchInferenceResultsByRig } from '@/api/calibrationApi';
import { TableSkeletonRows } from '@/components/LoadingSkeleton/LoadingSkeleton';
import { TestResultsProps, SortableColumn, SortConfig } from './types';

import {
  Card,
  Text,
  Title,
  Alert,
  Divider,
  Table,
  Checkbox,
} from '@mantine/core';

import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';

import { formatDisplayDateTime } from '@/utils/dateFormat';
import { formatFixedNumber } from '@/utils/numberFormat';
import { sampleSourceDisplayName } from '@/utils/sampleSources';
import { useSampleSourceCatalog } from '@/hooks/useSampleSourceCatalog';
import { useEffect, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';


export default function TestResultsComponent({
  rigId,
  rigData,
  selectedTestResult,
  onTestResultSelect,
  selectedRetortHistory,
}: TestResultsProps) {
  const { t, language } = useLanguage();
  const { sampleSources } = useSampleSourceCatalog();
  const [allTestResults, setAllTestResults] = useState<InferenceResult[]>([]);
  const [testResultsLoading, setTestResultsLoading] = useState(false);
  const [testResultsError, setTestResultsError] = useState<string | null>(null);
  const [testResultsSortConfig, setTestResultsSortConfig] = useState<SortConfig>({
    column: 'mud_density',
    direction: 'desc',
  });

  useEffect(() => {
    const loadTestResults = async () => {
      if (!rigId || isNaN(Number(rigId)) || !rigData) {
        setAllTestResults([]);
        return;
      }

      try {
        setTestResultsLoading(true);
        setTestResultsError(null);

        const response = await fetchInferenceResultsByRig(rigData.name);

        const processedData = response.data
          .filter((item) => item.rig_number === rigData.name)
          .map((item) => {
            return {
              ...item,
              date_time: `${item.date}T${item.time}`,
            };
          });

        setAllTestResults(processedData);
      } catch (error: unknown) {
        console.error('Error fetching test results:', error);
        setTestResultsError(t('calibration.loadTestResultsError'));
        setAllTestResults([]);
      } finally {
        setTestResultsLoading(false);
      }
    };

    loadTestResults();
  }, [rigId, rigData, t]);

  const handleTestResultSelect = (result: InferenceResult | null) => {
    onTestResultSelect(result);
  };

  const handleTestResultsSort = (column: SortableColumn) => {
    setTestResultsSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        column,
        direction: 'desc',
      };
    });
  };

  const getSortedTestResults = (results: InferenceResult[]): InferenceResult[] => {
    if (!testResultsSortConfig.column) return results;

    const sorted = [...results].sort((a, b) => {
      let aValue = 0;
      let bValue = 0;

      switch (testResultsSortConfig.column) {
        case 'mud_density':
          aValue = a.mud_density ?? 0;
          bValue = b.mud_density ?? 0;
          break;
        case 'date_time':
          aValue = a.date_time ? new Date(a.date_time).getTime() : 0;
          bValue = b.date_time ? new Date(b.date_time).getTime() : 0;
          break;
        case 'base_oil_vol_per_predicted_before_calibration':
          aValue = a.base_oil_vol_per_predicted_before_calibration ?? 0;
          bValue = b.base_oil_vol_per_predicted_before_calibration ?? 0;
          break;
        case 'water_vol_per_predicted_before_calibration':
          aValue = a.water_vol_per_predicted_before_calibration ?? 0;
          bValue = b.water_vol_per_predicted_before_calibration ?? 0;
          break;
        case 'base_oil_vol_per_predicted':
          aValue = a.base_oil_vol_per_predicted ?? 0;
          bValue = b.base_oil_vol_per_predicted ?? 0;
          break;
        case 'water_vol_per_predicted':
          aValue = a.water_vol_per_predicted ?? 0;
          bValue = b.water_vol_per_predicted ?? 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return testResultsSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return testResultsSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const testResults = getSortedTestResults([...allTestResults]);

  return (
    <Card withBorder padding="md" radius="md" className="rc-card">
      <Title order={2} mb="xs" className="rc-sectionTitle rc-sectionTitle--light">
        {t('calibration.testStep')}
      </Title>

      {selectedRetortHistory && (
        <Text size="sm" mb="sm" c="dimmed" ta="center">
          {t('calibration.matchingSourceHint')}
        </Text>
      )}

      <Divider my="md" className="rc-divider" />

      {testResultsError && (
        <Alert icon={<IconAlertCircle size="1rem" />} title={t('common.error')} color="red" mb="md" className="rc-alert">
          {testResultsError}
        </Alert>
      )}

      <div className="rc-tableContainer">
        <Table
          striped
          highlightOnHover
          withTableBorder
          className="rc-table rc-table--compact"
          aria-busy={testResultsLoading}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="rc-checkboxCell"></Table.Th>
              <Table.Th onClick={() => handleTestResultsSort('date_time')}>
                <Text fw={700}>{t('calibration.date')}</Text>
              </Table.Th>
              <Table.Th>
                <Text fw={700}>{t('calibration.well')}</Text>
              </Table.Th>
              <Table.Th onClick={() => handleTestResultsSort('mud_density')}>
                <Text fw={700}>{t('calibration.density')}</Text>
              </Table.Th>
              <Table.Th>
                <Text fw={700}>{t('results.sampleSource')}</Text>
              </Table.Th>
              <Table.Th onClick={() => handleTestResultsSort('base_oil_vol_per_predicted_before_calibration')}>
                <Text fw={700}>{t('calibration.predictedOil')}</Text>
              </Table.Th>
              <Table.Th onClick={() => handleTestResultsSort('water_vol_per_predicted_before_calibration')}>
                <Text fw={700}>{t('calibration.predictedWater')}</Text>
              </Table.Th>
              <Table.Th onClick={() => handleTestResultsSort('base_oil_vol_per_predicted')}>
                <Text fw={700}>{t('calibration.calibratedOil')}</Text>
              </Table.Th>
              <Table.Th onClick={() => handleTestResultsSort('water_vol_per_predicted')}>
                <Text fw={700}>{t('calibration.calibratedWater')}</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {testResultsLoading ? (
              <TableSkeletonRows columns={9} />
            ) : testResults.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={9} className="rc-emptyStateCell">
                  <Text c="dimmed">{t('calibration.noTestResults')}</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              testResults.map((item) => {
                const isSelected = selectedTestResult?.id === item.id;
                return (
                  <Table.Tr
                    key={item.id}
                    onClick={() => handleTestResultSelect(isSelected ? null : item)}
                    className={`rc-testResultsRow ${isSelected ? 'rc-testResultsRow--selected' : ''}`}
                  >
                    <Table.Td className="rc-checkboxCell">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleTestResultSelect(isSelected ? null : item)}
                        onClick={(e) => e.stopPropagation()}
                        classNames={{ input: 'rc-checkbox' }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDisplayDateTime(item.date_time, 'N/A')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.well_number ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {item.mud_density !== undefined && item.mud_density !== null
                          ? formatFixedNumber(item.mud_density, language, 2)
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {item.sample_source
                          ? sampleSourceDisplayName(
                            sampleSources,
                            item.sample_source,
                            language
                          )
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {item.base_oil_vol_per_predicted_before_calibration !== undefined && item.base_oil_vol_per_predicted_before_calibration !== null
                          ? formatFixedNumber(item.base_oil_vol_per_predicted_before_calibration, language, 2)
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {item.water_vol_per_predicted_before_calibration !== undefined && item.water_vol_per_predicted_before_calibration !== null
                          ? formatFixedNumber(item.water_vol_per_predicted_before_calibration, language, 2)
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {item.base_oil_vol_per_predicted !== undefined && item.base_oil_vol_per_predicted !== null
                          ? formatFixedNumber(item.base_oil_vol_per_predicted, language, 2)
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {item.water_vol_per_predicted !== undefined && item.water_vol_per_predicted !== null
                          ? formatFixedNumber(item.water_vol_per_predicted, language, 2)
                          : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </div>
    </Card>
  );
}
